import SaveAltRoundedIcon from "@mui/icons-material/SaveAltRounded";
import { IconButton, Stack } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import FileIcon from "@entrilia/shared/components/FileIcon";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import biApi from "../../../../../../../../../api/biApi";
import type { AttachedDocument } from "../../../../../../../../../api/types/svTypes";
import { formatFileLabel, normalizeExtension } from "../utils/transactionAttachmentsHelper";

interface AttachmentListItemProps {
  item: AttachedDocument;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}
const getDownloadInfo = withErrorHandling(biApi.getAttachedDocumentDownloadInfo);

const TransactionAttachmentListItem = ({ item, active, disabled, onClick }: AttachmentListItemProps) => {
  const [downloading, setDownloading] = useState(false);
  const { sendNotificationError } = useNotificationContext();

  const handleDownload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setDownloading(true);
    const [downloadInfo, error] = await getDownloadInfo(item.catalogueId);
    setDownloading(false);
    if (error) {
      logError(error, "[TransactionAttachmentListItem] handleDownload");
      sendNotificationError(error.message);
      return;
    }
    await downloadFileFromUrl(downloadInfo.downloadUrl);
  };

  return (
    <Stack
      direction="row"
      spacing={1.25}
      onClick={disabled ? undefined : onClick}
      sx={{
        alignItems: "center",
        px: 1,
        py: 0.375,
        mb: 1,
        cursor: disabled ? "default" : "pointer",
        border: 1,
        borderColor: active ? "primary.main" : "divider",
        borderRadius: 1,
        "&:hover": { bgcolor: disabled ? "inherit" : "action.hover" },
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <FileIcon fileExtension={normalizeExtension(item.fileExtension)} fontSize="small" />
      <TypographyTooltipEllipsis
        text={formatFileLabel(item)}
        typographyProps={{ sx: { flex: 1 } }}
        tooltipProps={{ disableInteractive: true }}
      />
      <IconButton size="small" loading={downloading} onClick={handleDownload} disabled={disabled}>
        <SaveAltRoundedIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

export default TransactionAttachmentListItem;
